import { keyBy as _keyBy, sortBy as _sortBy } from 'lodash/fp'

import {
  ActionAgent,
  ContextItem,
  Conversation,
  ConversationOutcomeClassification,
  ConversationOutcomeType,
  ConversationThreadContextDataItemType,
  ExecutionPlanAction,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'

import { AgentAction, AgentName } from './ai-conversation.commons'
import { ActionsDiff, AttachedFile, AttachedFileType } from './ai-conversation.dictionary'
import { ContextDataKeys, MessageType } from './ai-conversation.model'

import { ChatMessage } from '../types/ai.types'

/**
 * Builds message outcome ID from base message ID
 */
const buildMessageOutcomeId = (messageId: string): string => `${messageId}-outcome`

/**
 * Infers file type from context key and agent information
 */
const inferFileType = (key: string, agent?: ActionAgent): AttachedFileType => {
  // Check for BPMN based on agent and action
  if (agent?.agent === AgentName.AiAnalyst && agent?.action === AgentAction.CreateProcessFromBpmn) {
    return 'bpmn'
  }

  // Check for image based on key
  if (
    key === ContextDataKeys.DesignerReferenceImage ||
    key === ContextDataKeys.ProcessReferenceImage
  ) {
    return 'image'
  }

  // Default to document
  return 'document'
}

/**
 * Checks if a context item is a valid binary object reference
 */
const isValidBinaryObjectReference = (item: ContextItem): boolean =>
  item.itemType === ConversationThreadContextDataItemType.BinaryObject &&
  !!item.valueBinaryObject?.docID

/**
 * Extracts file references from a single action's context
 */
const extractFileReferencesFromAction = (action: ExecutionPlanAction): AttachedFile[] =>
  action.context?.items?.filter(isValidBinaryObjectReference).map((item) => ({
    id: item.valueBinaryObject.docID,
    key: item.key,
    type: inferFileType(item.key, action.agent),
  })) ?? []

/**
 * Extracts file references from execution plan action context
 */
const extractFileReferencesFromHint = (message: Message): AttachedFile[] =>
  message.executionPlan?.actions?.flatMap(extractFileReferencesFromAction) ?? []

/**
 * Whether the message was spawned by an agent execution plan. The backend sets
 * THREAD_CAUSED_BY_AGENT_PLAN on both the message context and the seed action
 * context, but only the message-level item survives a `GET /conversations/:id`
 * re-fetch — the action-level copy is stripped. Read it from the message so
 * rendering stays stable across the post-stream refetch and full reloads.
 */
const isAgentPlanMessage = (message: Message): boolean =>
  message.contextData.items.some((item) => item.key === ContextDataKeys.ThreadCausedByAgentPlan)

/**
 * Builds base message with original type from context data
 */
const buildBaseMessage = (message: Message): any => {
  const contextItems: Record<string, ContextItem> = _keyBy('key', message.contextData.items)

  const destinationSelector = contextItems[ContextDataKeys.UiDestinationSelector]?.valueGenericString

  // Extract file references from hint action context
  const fileReferences = extractFileReferencesFromHint(message)

  const hasAgentPlanContext = !!contextItems[ContextDataKeys.ThreadCausedByAgentPlan]

  // Parse destinationSelector with error handling
  let parsedDestinationSelector = null
  if (destinationSelector) {
    try {
      parsedDestinationSelector = JSON.parse(destinationSelector)
    } catch (error) {
      console.error('Failed to parse destinationSelector:', error)
    }
  }

  const firstAction = message.executionPlan?.actions?.[0]
  const isAssistantAction = firstAction?.agent?.agent === AgentName.AiAssistant
  const parsedExecutionPlanAction =
    hasAgentPlanContext && firstAction?.name && !isAssistantAction
      ? { name: firstAction.name, description: firstAction.description }
      : null

  // Parse dataModel with error handling
  let parsedDataModel = {}
  try {
    parsedDataModel = JSON.parse(
      contextItems[ContextDataKeys.UIAiDesignerSummaryChanges]?.valueGenericString ?? '{}'
    )
  } catch (error) {
    console.error('Failed to parse dataModel:', error)
  }

  return {
    id: message.id,
    message: message.prompt,
    type:
      contextItems[ContextDataKeys.UIMessageType]?.valueGenericString ??
      (hasAgentPlanContext ? MessageType.UserMsg : undefined),
    agent: firstAction?.agent?.agent,
    action: firstAction?.agent?.action,
    contextItems,
    destinationSelector: parsedDestinationSelector,
    executionPlanAction: parsedExecutionPlanAction,
    dataModel: parsedDataModel,
    attachedFiles: hasAgentPlanContext ? [] : fileReferences, // Don't show files if thread was caused by agent plan
  }
}

/**
 * Builds AI response message from textual outcome
 */
const buildAiResponseMessage = (message: Message): any | null => {
  const textualOutcome = message.outcome?.outcomes.find(
    (outcome) =>
      outcome.outcomeType === ConversationOutcomeType.Textual &&
      outcome.outcomeClassification === ConversationOutcomeClassification.Chat
  )

  if (!textualOutcome?.textual?.content) {
    return null
  }

  let messageContent = textualOutcome.textual.content
  try {
    messageContent = JSON.parse(textualOutcome.textual.content)?.response
  } catch (error) {}

  return {
    id: buildMessageOutcomeId(message.id),
    message: messageContent,
    type: MessageType.AiMsg,
  }
}

/**
 * Builds error user message ID from base message ID
 */
const buildMessageErrorId = (messageId: string): string => `${messageId}-error`

/**
 * Builds error user message if the message has an error outcome
 */
const buildErrorUserMessage = (message: Message): any | null => {
  const errorOutcome = message.outcome?.outcomes.find(
    (outcome) =>
      outcome.outcomeType === ConversationOutcomeType.Error ||
      outcome.outcomeClassification === ConversationOutcomeClassification.Error
  )

  if (!errorOutcome) {
    return null
  }

  return {
    id: buildMessageErrorId(message.id),
    message:
      'Seems like something went wrong with your request. I am waiting on further instructions.',
    type: MessageType.ErrorMsg,
  }
}

const isAssistantAgentPlanMessage = (message: Message): boolean => {
  const firstAction = message.executionPlan?.actions?.[0]
  return firstAction?.agent?.agent === AgentName.AiAssistant && isAgentPlanMessage(message)
}

/**
 * Whether the message yields any visible base bubble. The default thread's
 * externally-computed plan arrives as a SYSTEM message with empty prompt and no
 * context — it only carries an executionPlan for the plan service, so emitting a
 * base bubble for it renders an empty chat entry. Every renderable base field is
 * derived from the prompt, a contextData item, or a hint file reference, so the
 * absence of all three means there is nothing to show.
 */
const hasRenderableBaseContent = (message: Message): boolean =>
  !!message.prompt?.trim() ||
  message.contextData.items.length > 0 ||
  extractFileReferencesFromHint(message).length > 0

/**
 * Builds chat messages from a conversation message
 */
const buildChatMessagesFromMessage = (message: Message): any[] => {
  const messages: any[] = []

  if (!isAssistantAgentPlanMessage(message) && hasRenderableBaseContent(message)) {
    messages.push(buildBaseMessage(message))
  }

  const aiResponse = buildAiResponseMessage(message)
  if (aiResponse) {
    messages.push(aiResponse)
  }

  const errorMessage = buildErrorUserMessage(message)
  if (errorMessage) {
    messages.push(errorMessage)
  }

  return messages
}

/**
 * Builds the chat history from conversation data
 * @param conversation The active conversation
 * @param pendingMessage The pending message (if any)
 * @param hasMultiplePlans Whether there are multiple pending execution plans
 * @param enhancePlanDiff The execution plan diff (if any)
 * @param dataModelChanges The transformed data model changes to attach to user messages (if any)
 * @param isUiOutcomeFinished Whether the UI outcome has been kept (finished)
 * @returns Array of messages for the chat history
 */
function buildChatHistory(
  conversation: Conversation | undefined,
  pendingMessage: ChatMessage | null,
  hasMultiplePlans: boolean,
  enhancePlanDiff: Omit<ActionsDiff, 'displayTitle'> | null,
  dataModelChanges: ChatMessage['dataModel'] | null,
  isUiOutcomeFinished: boolean,
  isInProcess: boolean,
  developerDataModelChanges: ChatMessage['dataModel'] | null = null
): ChatMessage[] {
  const initialMessage: ChatMessage = {
    message: isInProcess
      ? 'I generate structured, actionable plans to coordinate process design, UI generation, and business logic implementation across the other agents.'
      : `I'm here to help with any questions you have about getting the most out of FlowX AI.`,
    type: MessageType.SystemMsg,
  }

  if (!conversation?.threads) {
    return [initialMessage, ...(pendingMessage ? [pendingMessage] : [])]
  }

  const threadMessages = _sortBy(
    (message) => message.auditData?.createdAt,
    conversation.threads.flatMap((thread) => thread.messages)
  )

  const mappedThreadMessages = threadMessages.flatMap((message) =>
    buildChatMessagesFromMessage(message)
  )

  // Attach data model changes to the most recent user message only if UI outcome is finished
  if (dataModelChanges && isUiOutcomeFinished && mappedThreadMessages.length > 0) {
    // Find all user messages (type === MessageType.UserMsg)
    const userMessages = mappedThreadMessages.filter((msg) => msg.type === MessageType.UserMsg)

    if (userMessages.length > 0) {
      // Attach to the most recent (last) user message
      const lastUserMessage = userMessages[userMessages.length - 1]
      lastUserMessage.dataModelSummary = dataModelChanges
    }
  }

  // Developer agent: the generated code may carry data-model changes. Surface them informationally
  // on the latest user message — no separate keep/discard; they follow the code's.
  if (developerDataModelChanges && mappedThreadMessages.length > 0) {
    const userMessages = mappedThreadMessages.filter((msg) => msg.type === MessageType.UserMsg)

    if (userMessages.length > 0) {
      userMessages[userMessages.length - 1].developerDataModelSummary = developerDataModelChanges
    }
  }

  const messages = [
    ...(mappedThreadMessages[0]?.type === MessageType.UserMsg ? [initialMessage] : []),
    ...mappedThreadMessages,
    ...(pendingMessage ? [pendingMessage] : []),
  ]

  // Add execution plan diff message if needed
  if (hasMultiplePlans && enhancePlanDiff) {
    messages.push({
      message: '',
      enhancePlanActionsDiffAction: enhancePlanDiff,
      type: MessageType.SystemMsg,
    })
  }

  return messages
}

export const ChatEntity = {
  buildChatHistory,
}
